import { axiosInstance } from './api';
import type { AdminTunnelCapability } from './adminTunnel';

/**
 * NVR (network video recorder) REST client.
 *
 * Mirrors the backend model in
 * `services/plugins/equipment_discovery/models.py:NVR` + the endpoints in
 * `services/plugins/equipment_discovery/api/nvrs.py`. The vendor field is
 * a strict choices list on the BE (only "hikvision" today); kept here as
 * the broader `string` to leave headroom when new vendors land.
 *
 * Password is returned in the GET payload by design — same convention as
 * `zoneRouter.ts`. Don't log it. The operator who can see the NVR card
 * can see its admin creds.
 *
 * Identity fields (`model`, `serial_number`, `firmware_version`,
 * `device_id`, `mac_address`) are populated by the discovery primitive
 * via PATCH with explicit values; no separate autofill endpoint exists.
 */

export interface NVRRecord {
  id: number;
  zone_id: number;
  ip_address: string;
  port: number;
  username: string;
  password: string;
  vendor: string;
  model: string;
  serial_number: string;
  firmware_version: string;
  device_id: string | null;
  mac_address: string;
  notes: string;
  admin_tunnel_capability?: AdminTunnelCapability | null;
  created_at: string;
  updated_at: string;
}

/** Writable fields on POST (`ip_address` is required) / PATCH. */
export type NVRUpsert = Partial<
  Pick<
    NVRRecord,
    | 'ip_address'
    | 'port'
    | 'username'
    | 'password'
    | 'vendor'
    | 'model'
    | 'serial_number'
    | 'firmware_version'
    | 'device_id'
    | 'mac_address'
    | 'notes'
  >
>;

const zoneUrl = (zoneId: number | string) =>
  `/equipment_discovery/zones/${zoneId}/nvrs/`;
const nvrUrl = (nvrId: number | string) =>
  `/equipment_discovery/nvrs/${nvrId}/`;

interface ZoneNVRsResponse {
  results: NVRRecord[];
}

export const listZoneNVRs = (zoneId: number | string) =>
  axiosInstance.get<ZoneNVRsResponse>(zoneUrl(zoneId));

export const createNVR = (zoneId: number | string, payload: NVRUpsert) =>
  axiosInstance.post<NVRRecord>(zoneUrl(zoneId), payload);

export const updateNVR = (nvrId: number | string, payload: NVRUpsert) =>
  axiosInstance.patch<NVRRecord>(nvrUrl(nvrId), payload);

export const deleteNVR = (nvrId: number | string) =>
  axiosInstance.delete<void>(nvrUrl(nvrId));

// ── NVR → camera channel import (#24) ──────────────────────────────
//
// Pulls the NVR's InputProxyChannel list via the jetson probe + creates
// Camera + CameraProviderBase rows for any (ip, channel) not already
// recorded. Returns the new + existing camera summaries plus per-channel
// errors. Idempotent — re-running after partial success only creates
// the rows that weren't there before.

export interface FetchCamerasNewCamera {
  id: number;
  name: string;
  ip_address: string;
  channel: number;
  source: string;
}

export interface FetchCamerasExistingCamera {
  id: number;
  name: string;
  ip_address: string;
  channel: number;
}

export interface FetchCamerasResponse {
  new_cameras: FetchCamerasNewCamera[];
  existing_cameras: FetchCamerasExistingCamera[];
  errors: string[];
}

export const fetchNvrCameras = (nvrId: number | string) =>
  axiosInstance.post<FetchCamerasResponse>(
    `/equipment_discovery/nvrs/${nvrId}/fetch-cameras/`,
  );
